(function() {
  'use strict';

  // Check if Black Friday Auto-Add is enabled in theme settings
  const isEnabled = document.documentElement.dataset.bfAutoAddEnabled === 'true';
  if (!isEnabled) return;

  // Check customer group visibility
  const customerGroup = document.documentElement.dataset.bfAutoAddCustomerGroup || 'none';
  const hasLoyalty = document.documentElement.dataset.customerHasLoyalty === 'true';
  
  // If restricted to loyalty members only, check if customer qualifies
  if (customerGroup === 'loyalty' && !hasLoyalty) {
    console.log('[BF Auto-Add] Feature restricted to loyalty members only');
    return;
  }

  const STORAGE_KEY = 'bf_auto_add_enabled';
  const PROCESSING_FLAG = 'bf_processing_gwp_1';
  
  /**
   * Gets the auto-add preference from localStorage
   * @returns {boolean}
   */
  function isAutoAddEnabled() {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  }

  /**
   * Sets the auto-add preference in localStorage
   * @param {boolean} enabled
   */
  function setAutoAddEnabled(enabled) {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  }

  /**
   * Shows the loading overlay
   */
  function showOverlay() {
    const overlay = document.getElementById('bf-auto-add-overlay');
    if (overlay) {
      overlay.removeAttribute('hidden');
    }
  }

  /**
   * Hides the loading overlay
   */
  function hideOverlay() {
    const overlay = document.getElementById('bf-auto-add-overlay');
    if (overlay) {
      overlay.setAttribute('hidden', '');
    }
  }

  /**
   * Gets all active GWP offers sorted by threshold (highest first)
   * @returns {Array<{element: Element, threshold: number, button: HTMLButtonElement}>}
   */
  function getActiveGwpOffers() {
    const offers = document.querySelectorAll('.gwp-offers .custom-gwp-offer');
    const activeOffers = [];

    offers.forEach((offerEl) => {
      const btn = offerEl.querySelector('.custom-gwp-offer__btn');
      if (!btn || btn.disabled) return;

      const threshold = Number(offerEl.getAttribute('data-threshold') || 0);
      
      // Check if offer is eligible (not ineligible class)
      if (offerEl.classList.contains('is-ineligible')) return;
      
      activeOffers.push({ element: offerEl, threshold, button: btn });
    });

    // Sort by threshold descending (highest first)
    activeOffers.sort((a, b) => b.threshold - a.threshold);
    
    return activeOffers;
  }

  /**
   * Gets the current GWP in cart (if any)
   * @returns {Promise<{group: string, threshold: number} | null>}
   */
  async function getCurrentGwpInCart() {
    try {
      const cart = await fetch('/cart.js', { 
        headers: { 'Accept': 'application/json' } 
      }).then(r => r.json());

      const items = Array.isArray(cart?.items) ? cart.items : [];
      
      for (const item of items) {
        const props = item.properties || {};
        if (props._is_gwp === 'true' && props._gwp_group) {
          const group = String(props._gwp_group);
          
          // Find the matching offer element to get its threshold
          const gwpEl = document.querySelector(`.custom-gwp-offer[data-gwp-group="${CSS.escape(group)}"]`);
          if (gwpEl) {
            const threshold = Number(gwpEl.getAttribute('data-threshold') || 0);
            return { group, threshold };
          }
        }
      }
      
      return null;
    } catch (e) {
      console.error('[BF Auto-Add] Error checking cart:', e);
      return null;
    }
  }

  /**
   * Automatically adds the best available GWP offer
   */
  async function autoAddBestGwp() {
    // Check if user has disabled auto-add
    if (!isAutoAddEnabled()) {
      console.log('[BF Auto-Add] Auto-add is disabled by user');
      return;
    }

    // Check if we're already processing to avoid race conditions
    if (sessionStorage.getItem(PROCESSING_FLAG) === 'true') {
      console.log('[BF Auto-Add] Already processing, skipping');
      return;
    }

    try {
      sessionStorage.setItem(PROCESSING_FLAG, 'true');
      
      // Safety timeout: if page doesn't reload within 10 seconds, clear flag and hide overlay
      const safetyTimeout = setTimeout(() => {
        console.warn('[BF Auto-Add] Safety timeout triggered - clearing processing flag');
        sessionStorage.removeItem(PROCESSING_FLAG);
        hideOverlay();
      }, 6000);
      
      const activeOffers = getActiveGwpOffers();
      if (activeOffers.length === 0) {
        console.log('[BF Auto-Add] No active GWP offers available');
        clearTimeout(safetyTimeout);
        sessionStorage.removeItem(PROCESSING_FLAG);
        return;
      }

      const currentGwp = await getCurrentGwpInCart();
      const bestOffer = activeOffers[0];

      // If there's no GWP in cart, add the best one
      if (!currentGwp) {
        console.log('[BF Auto-Add] No GWP in cart, adding best offer with threshold:', bestOffer.threshold);
        showOverlay();
        bestOffer.button.click();
        // Don't clear timeout here - let it run as failsafe
        return;
      }

      // If there's a better offer available (higher threshold), upgrade
      if (bestOffer.threshold > currentGwp.threshold) {
        console.log('[BF Auto-Add] Upgrading from threshold', currentGwp.threshold, 'to', bestOffer.threshold);
        showOverlay();
        bestOffer.button.click();
        // Don't clear timeout here - let it run as failsafe
        return;
      }

      console.log('[BF Auto-Add] Current GWP is already the best available');
      // Clear the flag immediately since no action was taken
      clearTimeout(safetyTimeout);
      sessionStorage.removeItem(PROCESSING_FLAG);
    } catch (e) {
      console.error('[BF Auto-Add] Error in autoAddBestGwp:', e);
      hideOverlay();
      sessionStorage.removeItem(PROCESSING_FLAG);
    }
  }

  /**
   * Removes all GWP items from cart
   */
  async function removeAllGwpFromCart() {
    try {
      const cart = await fetch('/cart.js', { 
        headers: { 'Accept': 'application/json' } 
      }).then(r => r.json());

      const items = Array.isArray(cart?.items) ? cart.items : [];
      const gwpLines = [];

      // Find all GWP items
      items.forEach((item, index) => {
        const props = item.properties || {};
        if (props._is_gwp === 'true') {
          gwpLines.push(index + 1); // Line numbers are 1-indexed
        }
      });

      if (gwpLines.length === 0) {
        console.log('[BF Auto-Add] No GWP items to remove');
        return;
      }

      console.log('[BF Auto-Add] Removing', gwpLines.length, 'GWP item(s) from cart');
      showOverlay();

      // Remove all GWP items (highest index first to avoid index shifting)
      gwpLines.sort((a, b) => b - a);
      const endpoint = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_change_url) 
        ? Theme.routes.cart_change_url 
        : '/cart/change.js';

      for (const line of gwpLines) {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 })
        });
      }

      // Reload page to refresh cart display
      window.location.reload();
    } catch (e) {
      console.error('[BF Auto-Add] Error removing GWP items:', e);
      hideOverlay();
    }
  }

  /**
   * Initialize the toggle functionality
   */
  function initToggle() {
    const toggleInput = document.getElementById('bf-auto-add-toggle-input');
    if (!toggleInput) return;

    // Set initial state from localStorage
    toggleInput.checked = isAutoAddEnabled();

    // Listen for changes
    toggleInput.addEventListener('change', (e) => {
      const enabled = e.target.checked;
      setAutoAddEnabled(enabled);
      console.log('[BF Auto-Add] User toggled auto-add:', enabled ? 'ON' : 'OFF');

      if (enabled) {
        // If user enables it and there's no GWP in cart, trigger auto-add
        setTimeout(() => autoAddBestGwp(), 300);
      } else {
        // If user disables it, remove any existing GWP items
        removeAllGwpFromCart();
      }
    });
  }

  /**
   * Initialize on page load
   */
  function init() {
    console.log('[BF Auto-Add] Initializing...');
    
    // Hide overlay immediately on load in case it was shown before page reload
    hideOverlay();
    
    // Clear any stale processing flag on init
    sessionStorage.removeItem(PROCESSING_FLAG);
    
    initToggle();

    // Run auto-add on page load only
    // Since GWP system forces page reload after changes, no need to listen to cart:update
    const runAutoAdd = () => {
      setTimeout(() => autoAddBestGwp(), 500);
    };

    // Initial run on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runAutoAdd);
    } else {
      runAutoAdd();
    }
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
